import { BenefitState, type HeldBenefit } from '@/services/myBenefits'

/**
 * The three groups a member's benefits fall into, in the order they are shown.
 *
 * Named for what the member can DO, not for the server's state machine, because that is what
 * decides where a row belongs and how much room it deserves:
 * - `claimable` — still to spend. The only group with a button.
 * - `claimed` — spent at a target, still to use. Carries the redemption instructions.
 * - `history` — finished. Nothing to do, and collapsed by default.
 *
 * The server's five states map onto these three: `AVAILABLE` is claimable; `ASSIGNED` and
 * `PENDING_STEP` are claimed (one is ready, the other is waiting on the target, but both are live
 * and neither is actionable by the member); `REDEEMED` and `EXPIRED` are history.
 */
export interface BenefitSections {
  claimable: HeldBenefit[]
  claimed: HeldBenefit[]
  history: HeldBenefit[]
}

/**
 * Sort a member's benefits into the three groups.
 *
 * ## An unrecognized state is shown, never archived
 *
 * A newer API can send a state this deploy has never heard of. Such a row lands in `claimed` —
 * visible, with no claim button — rather than in the collapsed history, because the failure modes
 * are not symmetrical: showing a member something they cannot act on is a puzzle, while hiding a
 * benefit they still hold inside a closed accordion is a benefit they never find.
 */
export const groupBenefits = (benefits: HeldBenefit[]): BenefitSections => {
  const sections: BenefitSections = { claimable: [], claimed: [], history: [] }

  benefits.forEach((benefit) => {
    switch (benefit.state) {
      case BenefitState.Available:
        sections.claimable.push(benefit)
        break
      case BenefitState.Redeemed:
      case BenefitState.Expired:
        sections.history.push(benefit)
        break
      default:
        sections.claimed.push(benefit)
        break
    }
  })

  return sections
}

/**
 * Whether a benefit's redemption block should start open.
 *
 * Open while there is something to do with it, closed once there is not. A used or expired benefit
 * still carries its instructions — a member reviewing what they spent should be able to see them —
 * but unfurling a QR code for a claim settled last month pushes every other row off the page.
 *
 * Deliberately keyed on the STATE rather than on which section the row is in, so a card rendered
 * anywhere else answers the same way.
 */
export const startsExpanded = (benefit: HeldBenefit): boolean =>
  benefit.state !== BenefitState.Redeemed && benefit.state !== BenefitState.Expired
